import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS, getImageUrl } from '../config/api';

const Gallery = () => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [userLikes, setUserLikes] = useState(new Set());

  // Get current user info (could be student, teacher, admin, etc.)
  const getCurrentUser = () => {
    // Try different user types
    const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
    const studentUser = JSON.parse(localStorage.getItem('studentUser') || '{}');
    const teacherUser = JSON.parse(localStorage.getItem('teacherUser') || '{}');
    const staffUser = JSON.parse(localStorage.getItem('staffUser') || '{}');

    if (adminUser.email) return { id: adminUser.id, name: adminUser.name, type: 'admin' };
    if (studentUser.username) return { id: studentUser.username, name: `${studentUser.firstName} ${studentUser.lastName}`, type: 'student' };
    if (teacherUser.email) return { id: teacherUser.email, name: teacherUser.name, type: 'teacher' };
    if (staffUser.email) return { id: staffUser.email, name: staffUser.name, type: 'staff' };

    return null; // Anonymous user
  };

  const currentUser = getCurrentUser();

  useEffect(() => {
    loadGallery();
  }, []);

  const loadGallery = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/gallery/media`);
      if (response.ok) {
        const data = await response.json();
        // Only show active media
        const activeMedia = data.filter(item => item.isActive);
        setGallery(activeMedia);

        // Track user's likes
        if (currentUser) {
          const userLikeIds = new Set();
          activeMedia.forEach(item => {
            const likeIdentifier = `${currentUser.type}-${currentUser.id}`;
            if (item.likedBy.includes(likeIdentifier)) {
              userLikeIds.add(item.id);
            }
          });
          setUserLikes(userLikeIds);
        }
      }
    } catch (error) {
      console.error('Error loading gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (mediaId) => {
    if (!currentUser) {
      alert('Please log in to like media');
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/gallery/media/${mediaId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: currentUser.id,
          userType: currentUser.type
        })
      });

      if (response.ok) {
        const result = await response.json();

        // Update local state
        setGallery(prevGallery =>
          prevGallery.map(item =>
            item.id === mediaId
              ? { ...item, likes: result.likes }
              : item
          )
        );

        // Update user's like status
        if (result.isLiked) {
          setUserLikes(prev => new Set([...prev, mediaId]));
        } else {
          setUserLikes(prev => {
            const newSet = new Set(prev);
            newSet.delete(mediaId);
            return newSet;
          });
        }
      }
    } catch (error) {
      console.error('Error liking media:', error);
      alert('Failed to like media');
    }
  };

  const handleComment = async (mediaId, comment) => {
    if (!currentUser) {
      alert('Please log in to comment');
      return;
    }

    if (!comment.trim()) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/gallery/media/${mediaId}/comment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: currentUser.id,
          userName: currentUser.name,
          userType: currentUser.type,
          comment: comment.trim()
        })
      });

      if (response.ok) {
        const result = await response.json();

        // Update local state
        setGallery(prevGallery =>
          prevGallery.map(item =>
            item.id === mediaId
              ? { ...item, comments: [...item.comments, result.comment] }
              : item
          )
        );

        // Close comment modal and reset
        setSelectedMedia(null);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    }
  };

  const handleView = async (mediaId) => {
    try {
      await fetch(`${API_ENDPOINTS.BASE_URL}/api/gallery/media/${mediaId}/view`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Error updating view count:', error);
    }
  };

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'events', label: 'Events' },
    { value: 'sports', label: 'Sports' },
    { value: 'academic', label: 'Academic' },
    { value: 'celebrations', label: 'Celebrations' },
    { value: 'facilities', label: 'Facilities' }
  ];

  const filteredGallery = selectedCategory === 'all'
    ? gallery
    : gallery.filter(item => item.category === selectedCategory);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <section id="gallery" className="py-12 px-6 bg-white text-blue-900">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2">Loading gallery...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="gallery" className="py-12 px-6 bg-white text-blue-900">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl font-bold mb-8 text-center text-blue-900">
          Creative Town Bilingual School Gallery
        </h2>

        {/* Category Filter */}
        <div className="mb-8 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              selectedCategory === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All
          </button>
          {categories.map(cat => (
            <button
              key={cat.value}
              onClick={() => setSelectedCategory(cat.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === cat.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {filteredGallery.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No media found in this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {filteredGallery.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="relative">
                  {item.fileType === 'image' ? (
                    <img
                      src={getImageUrl(`${item.filePath}`)}
                      alt={item.title}
                      className="w-full h-64 object-cover cursor-pointer hover:opacity-90 transition duration-300"
                      onClick={() => {
                        setSelectedMedia(item);
                        handleView(item.id);
                      }}
                    />
                  ) : (
                    <video
                      src={getImageUrl(`${item.filePath}`)}
                      className="w-full h-64 object-cover cursor-pointer"
                      onClick={() => {
                        setSelectedMedia(item);
                        handleView(item.id);
                      }}
                      muted
                    />
                  )}

                  <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs">
                    {item.fileType === 'video' ? 'Video' : '📷'} {categories.find(c => c.value === item.category)?.label}
                  </div>
                </div>

                <div className="p-4">
                  <h3 className="font-bold text-gray-800 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">{item.description}</p>

                  {/* Social Media Actions */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => handleLike(item.id)}
                        className={`flex items-center space-x-1 text-sm transition-colors ${
                          userLikes.has(item.id)
                            ? 'text-red-600 hover:text-red-700'
                            : 'text-gray-600 hover:text-red-600'
                        }`}
                      >
                        <span>{userLikes.has(item.id) ? '❤️' : '🤍'}</span>
                        <span>{item.likes}</span>
                      </button>

                      <button
                        onClick={() => setSelectedMedia(item)}
                        className="flex items-center space-x-1 text-sm text-gray-600 hover:text-blue-600 transition-colors"
                      >
                        <span>💬</span>
                        <span>{item.comments.length}</span>
                      </button>

                      <div className="flex items-center space-x-1 text-sm text-gray-600">
                        <span>👁️</span>
                        <span>{item.views}</span>
                      </div>
                    </div>

                    {/* Share Button */}
                    <button
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({
                            title: item.title,
                            text: item.description,
                            url: window.location.href
                          });
                        } else {
                          navigator.clipboard.writeText(window.location.href);
                          alert('Link copied to clipboard!');
                        }
                      }}
                      className="text-gray-600 hover:text-blue-600 transition-colors"
                      title="Share"
                    >
                      Share
                    </button>
                  </div>

                  <div className="text-xs text-gray-500">
                    {new Date(item.uploadDate).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Media Detail Modal */}
        {selectedMedia && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-gray-900">{selectedMedia.title}</h3>
                  <button
                    onClick={() => setSelectedMedia(null)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <div className="mb-4">
                  {selectedMedia.fileType === 'image' ? (
                    <img
                      src={getImageUrl(`${selectedMedia.filePath}`)}
                      alt={selectedMedia.title}
                      className="w-full max-h-96 object-contain rounded"
                    />
                  ) : (
                    <video
                      src={getImageUrl(`${selectedMedia.filePath}`)}
                      controls
                      className="w-full max-h-96 rounded"
                    />
                  )}
                </div>

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <p><strong>Description:</strong> {selectedMedia.description}</p>
                  <p><strong>Category:</strong> {categories.find(c => c.value === selectedMedia.category)?.label}</p>
                  <p><strong>Uploaded by:</strong> {selectedMedia.uploadedBy}</p>
                  <p><strong>Upload date:</strong> {new Date(selectedMedia.uploadDate).toLocaleString()}</p>
                  <p><strong>File size:</strong> {formatFileSize(selectedMedia.size)}</p>
                </div>

                {/* Social Actions */}
                <div className="flex items-center space-x-4 mb-4 p-3 bg-gray-50 rounded">
                  <button
                    onClick={() => handleLike(selectedMedia.id)}
                    className={`flex items-center space-x-2 px-3 py-1 rounded transition-colors ${
                      userLikes.has(selectedMedia.id)
                        ? 'bg-red-100 text-red-600'
                        : 'bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-600'
                    }`}
                  >
                    <span>{userLikes.has(selectedMedia.id) ? '❤️' : '🤍'}</span>
                    <span>{selectedMedia.likes} likes</span>
                  </button>

                  <button
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({
                          title: selectedMedia.title,
                          text: selectedMedia.description,
                          url: window.location.href
                        });
                      } else {
                        navigator.clipboard.writeText(window.location.href);
                        alert('Link copied to clipboard!');
                      }
                    }}
                    className="flex items-center space-x-2 px-3 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors"
                  >
                    <span>Share</span>
                    <span>Share</span>
                  </button>
                </div>

                {/* Comments Section */}
                <div className="border-t pt-4">
                  <h4 className="font-bold text-gray-800 mb-3">Comments ({selectedMedia.comments.length})</h4>

                  {selectedMedia.comments.length === 0 ? (
                    <p className="text-gray-500 text-sm mb-4">No comments yet.</p>
                  ) : (
                    <div className="space-y-3 max-h-40 overflow-y-auto mb-4">
                      {selectedMedia.comments.map((comment) => (
                        <div key={comment.id} className="bg-gray-50 rounded p-3">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="font-medium text-gray-800 text-sm">{comment.userName}</p>
                              <p className="text-gray-600 text-sm">{comment.comment}</p>
                            </div>
                            <span className="text-xs text-gray-500 ml-2">
                              {new Date(comment.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Comment */}
                  {currentUser && (
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        placeholder="Write a comment..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleComment(selectedMedia.id, e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                      <button
                        onClick={(e) => {
                          const input = e.target.previousElementSibling;
                          handleComment(selectedMedia.id, input.value);
                          input.value = '';
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-300 text-sm"
                      >
                        Comment
                      </button>
                    </div>
                  )}

                  {!currentUser && (
                    <p className="text-gray-500 text-sm text-center py-2">
                      Please log in to comment
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
